import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import type { RollApplication } from "../models/RollApplication";
import { CHEMICAL_FIELDS } from "../models/RollApplication";
import { RollMaterialSelector } from "./RollMaterialSelector";

interface FormErrors {
	Name?: string;
}

interface RollApplicationDetailViewerProps {
	application: RollApplication | null;
	existingApplications: RollApplication[];
	isSaving: boolean;
	onSave: (app: RollApplication) => void;
	onClose: () => void;
}

export const RollApplicationDetailViewer = ({
	application,
	existingApplications,
	isSaving,
	onSave,
	onClose,
}: RollApplicationDetailViewerProps) => {
	const [formData, setFormData] = useState<RollApplication | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewApplication = !application?.Id;

	const validateForm = useMemo(() => {
		return (data: RollApplication | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingApplications.some(
					(a) =>
						a.Id !== data.Id &&
						a.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingApplications]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewApplication && isFormValid;

	useEffect(() => {
		if (application) {
			setFormData(sanitizeNullStrings({ ...application }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [application]);

	const handleChange = (field: keyof RollApplication, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof RollApplication) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof RollApplication, value: unknown) => {
		handleBlur(field);

		if (isNewApplication) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = application?.[field];
		if (originalValue !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleSelectChange = (field: keyof RollApplication, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewApplication) return;

		if (application?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (
		field: keyof RollApplication,
		rawValue: string,
		max?: number,
	) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		if (value != null && value < 0) return;
		if (max != null && value != null && value > max) return;
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!application) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a roll application to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewApplication ? "New Roll Application" : formData.Name}
				active={isNewApplication ? undefined : application.Active}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNewApplication ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : undefined
				}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Basic Information" icon={<InfoOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Name"
									value={formData.Name}
									onChange={(e) => handleChange("Name", e.target.value)}
									onBlur={(e) => handleBlurSave("Name", e.target.value)}
									fullWidth
									size="small"
									required
									error={touched.Name && !!errors.Name}
									helperText={touched.Name && errors.Name}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<RollMaterialSelector
									value={formData.RollMaterialId}
									onChange={(value) =>
										handleSelectChange("RollMaterialId", value)
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.Active}
											onChange={(e) =>
												handleSelectChange("Active", e.target.checked)
											}
											size="small"
										/>
									}
									label="Active"
									sx={{ ml: 0 }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection
						title="Hardness Specifications"
						icon={<SpeedOutlinedIcon />}
					>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Maximum Hardness"
									type="number"
									value={formData.MaximumHardness ?? ""}
									onChange={(e) =>
										handleChange(
											"MaximumHardness",
											e.target.value === ""
												? null
												: Number.parseFloat(e.target.value),
										)
									}
									onBlur={(e) =>
										handleNumericBlurSave("MaximumHardness", e.target.value)
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{
										inputLabel: { shrink: true },
										htmlInput: { step: 1 },
									}}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Minimum Hardness"
									type="number"
									value={formData.MinimumHardness ?? ""}
									onChange={(e) =>
										handleChange(
											"MinimumHardness",
											e.target.value === ""
												? null
												: Number.parseFloat(e.target.value),
										)
									}
									onBlur={(e) =>
										handleNumericBlurSave("MinimumHardness", e.target.value)
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{
										inputLabel: { shrink: true },
										htmlInput: { step: 1 },
									}}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Comments" icon={<NotesOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Application Comments"
									value={formData.ApplicationComments ?? ""}
									onChange={(e) =>
										handleChange("ApplicationComments", e.target.value || null)
									}
									onBlur={(e) =>
										handleBlurSave(
											"ApplicationComments",
											e.target.value || null,
										)
									}
									fullWidth
									size="small"
									multiline
									rows={3}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection
						title="Chemical Composition"
						icon={<ScienceOutlinedIcon />}
					>
						<Grid2 container spacing={1}>
							{CHEMICAL_FIELDS.map((field) => {
								const fieldValue = formData[field as keyof RollApplication] as
									| number
									| null;
								const outOfRange =
									fieldValue != null && (fieldValue < 0 || fieldValue > 99.999);
								return (
									<Grid2 key={field} size={{ xs: 6, sm: 4, md: 2.4 }}>
										<TextField
											label={field}
											type="number"
											value={fieldValue ?? ""}
											onChange={(e) =>
												handleChange(
													field as keyof RollApplication,
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave(
													field as keyof RollApplication,
													e.target.value,
													99.999,
												)
											}
											fullWidth
											size="small"
											error={outOfRange}
											helperText={outOfRange ? "Range: 0 - 99.999" : undefined}
											sx={EDITABLE_FIELD_SX}
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.001, min: 0, max: 99.999 },
											}}
										/>
									</Grid2>
								);
							})}
						</Grid2>
					</DetailSection>
				</Box>
			</DetailBody>
		</Box>
	);
};
